import React, {
  useMemo,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { CalendarApi } from '@fullcalendar/core';
import type {
  EventInput,
  EventContentArg,
  EventClickArg,
  DatesSetArg,
} from '@fullcalendar/core';
import { Flex, Tooltip, Typography } from 'antd';

import type {
  CalendarDayAggregate,
  CalendarHourAggregate,
  CalendarShipmentItem,
  CalendarViewBy,
} from '../../types';
import {
  ShipmentAlertType,
  getCalendarShipmentBorderColor,
  AlertIcon,
} from './CalendarHelpers';

import styles from './CalendarView.module.css';
import CalendarToolbar from '../CalendarToolbar';
import { CheckOutlined } from '@ant-design/icons';
import { getCustomerReference } from '../shipment/shipmentTableUtils';

import { useToken } from '@/common/helper';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ColorLegends from './ColorLegends';
import type { CalendarLegendLegType } from './ColorLegends';

dayjs.extend(utc);
dayjs.extend(timezone);

type TeamDot = { color: string; name?: string };

type CalendarTeam = {
  id: string;
  name: string;
  color: string;
};

const DEFAULT_SCROLL_TIME = '00:00:0';

type HasEventDatetime = { event_datetime?: string | null };

function getVisibleWeekShipmentsFromHourAggregates(
  filteredData: CalendarDayAggregate[],
  rangeStart: Date,
  rangeEnd: Date
): CalendarShipmentItem[] {
  const start = dayjs(rangeStart);
  const end = dayjs(rangeEnd);

  return (filteredData ?? [])
    .filter((d) => {
      const day = dayjs(d.date, 'YYYY-MM-DD');
      return day.isSameOrAfter(start, 'day') && day.isBefore(end, 'day');
    })
    .flatMap((d) => d.hour_aggregates ?? [])
    .flatMap((h) => h.top_shipments ?? []);
}

function getScrollTimeFromVisibleWeekShipments(
  shipments: CalendarShipmentItem[],
  fallback: string = DEFAULT_SCROLL_TIME
): string {
  const timed = shipments.filter(
    (
      s
    ): s is CalendarShipmentItem &
      Required<Pick<HasEventDatetime, 'event_datetime'>> => !!s.event_datetime
  );

  if (timed.length === 0) return fallback;

  const firstByTimeOfDay = timed.reduce((min, cur) => {
    const mt = dayjs(min.event_datetime).format('HH:mm:ss');
    const ct = dayjs(cur.event_datetime).format('HH:mm:ss');
    return ct < mt ? cur : min;
  }, timed[0]);

  return dayjs(firstByTimeOfDay.event_datetime).format('HH:00:00');
}

const sortByEventTimeAsc = (list: CalendarShipmentItem[]) => {
  return [...(list ?? [])].sort((a, b) => {
    const av = dayjs(a.event_datetime ?? a.event_date).valueOf();
    const bv = dayjs(b.event_datetime ?? b.event_date).valueOf();
    return av - bv;
  });
};
function TeamDots({
  teams,
  maxVisible = 2,
  tableView,
}: {
  teams: TeamDot[];
  maxVisible?: number;
  tableView?: boolean;
}) {
  const visible = teams.slice(0, maxVisible);
  const remaining = teams.slice(maxVisible);

  const tooltipContent =
    remaining.length > 0 ? (
      <div className={styles.teamTooltip}>
        {remaining.map((t, idx) => (
          <div key={idx} className={styles.teamTooltipRow}>
            <span
              className={styles.teamDot}
              style={{ backgroundColor: t.color }}
            />
            <span className={styles.teamTooltipName}>{t.name ?? 'Team'}</span>
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div className={styles.teamDots}>
      {visible.map((t, idx) => (
        <span
          key={idx}
          className={styles.teamDot}
          style={{ backgroundColor: t.color }}
          title={t.name ?? ''}
        />
      ))}
      {remaining.length > 0 && (
        <Tooltip placement='top' title={tooltipContent}>
          <span className={` ${tableView && 'sm-d-none'}  ${styles.teamMore}`}>
            +{remaining.length}
          </span>
        </Tooltip>
      )}
    </div>
  );
}

function mapTeams(shipment: CalendarShipmentItem): TeamDot[] {
  const teams = (shipment.teams ?? []) as CalendarTeam[];
  const nonDefault = teams.filter(
    (t) => t.name?.trim().toLowerCase() !== 'default'
  );
  const display = nonDefault.length > 0 ? nonDefault : teams;
  return display.map((t) => ({
    color: t.color,
    name: t.name,
  }));
}

type tokenTypes = {
  time_zone_enable?: boolean;
  user_time_zone?: string;
};

function getUserTimeZoneString(decodedToken: tokenTypes) {
  const tz =
    decodedToken?.time_zone_enable && decodedToken?.user_time_zone
      ? decodedToken.user_time_zone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;

  const offsetMinutes = dayjs().tz(tz).utcOffset();
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes >= 0 ? '+' : '-';

  const formatted = `GMT ${sign}${hours.toString().padStart(2, '0')}${
    minutes > 0 ? `:${minutes.toString().padStart(2, '0')}` : ''
  }`;

  return formatted;
}

type CalendarViewProps = {
  tableView?: boolean;
  calendarApiRef?: React.MutableRefObject<CalendarApi | null>;
  onMoreClick?: (args: {
    dateStr: string;
    eventDatetime?: string | null;
    shipments: CalendarShipmentItem[];
    slotStart?: string | null;
    slotEnd?: string | null;
    source?: 'header' | 'slot' | 'month';
  }) => void;

  viewMode: 'today' | 'week' | 'month';
  filteredData: CalendarDayAggregate[];

  checked?: boolean;
  selectedIds?: string[];
  isSelectAllMode?: boolean;
  excludedIds?: string[];
  onShipmentClick?: (
    shipmentId: string,
    alertName: string | undefined,
    shipment: CalendarShipmentItem
  ) => void;
  onToggleSelect?: (
    shipment: CalendarShipmentItem,
    nextSelected: boolean
  ) => void;
  viewBy: CalendarViewBy;
  setViewBy: (v: CalendarViewBy) => void;
  onRangeChange?: (arg: unknown) => void;
  legType?: CalendarLegendLegType;
};
const REF_TRUNCATE_LEN = 12;

function truncateRef(value: unknown, maxLen: number) {
  const ref = String(value ?? '');
  if (ref.length <= maxLen)
    return { short: ref, isTruncated: false, full: ref };
  return { short: `${ref.slice(0, maxLen)}...`, isTruncated: true, full: ref };
}

function ReferenceTooltipContent({
  shipment,
}: {
  shipment: CalendarShipmentItem;
}) {
  const reference = getCustomerReference(shipment);
  const teams = mapTeams(shipment);

  const showAlert =
    shipment.type === ShipmentAlertType.CRITICAL ||
    shipment.type === ShipmentAlertType.WARNING;

  return (
    <div
      style={{
        backgroundColor: 'var(--surface-container)',
        color: 'var(--text-primary)',
        padding: '12px',
        borderRadius: 8,
        border: '1px solid #F3F3F3',
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        minWidth: 200,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, lineHeight: '22px' }}>
        {reference}
      </div>

      {showAlert && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <AlertIcon type={shipment.type} />
          </span>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            {shipment.alert_name ?? ''}
          </span>
        </div>
      )}

      {teams.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {teams.map((t, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  minWidth: 10,
                  flexShrink: 0,
                  borderRadius: '50%',
                  background: t.color,
                  display: 'inline-block',
                  marginTop: 2,
                }}
              />

              <span
                style={{
                  fontSize: 12,
                  minWidth: 0,
                  flex: 1,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {t.name ?? 'Team'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function CalendarView({
  tableView = false,
  onMoreClick,
  viewMode,
  viewBy,
  setViewBy,
  filteredData,
  checked = false,
  calendarApiRef,
  selectedIds = [],
  isSelectAllMode = false,
  excludedIds = [],
  onShipmentClick,
  onToggleSelect,
  onRangeChange,
  legType = 'CL',
}: CalendarViewProps) {
  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();
  const enableCustomerLeg = Boolean(decodedToken?.enablecustomerleg);
  const userTimeZoneString = getUserTimeZoneString(decodedToken);

  const calRef = useRef<FullCalendar | null>(null);
  const didInitDatesSetRef = useRef(false);
  const [internalView, setInternalView] = React.useState(
    viewMode === 'month'
      ? 'dayGridMonth'
      : viewMode === 'week'
        ? 'timeGridWeek'
        : 'timeGridDay'
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      if (arg.view.type !== internalView) {
        setInternalView(arg.view.type as typeof internalView);
      }
      onRangeChange?.({
        start: arg.start,
        end: arg.end,
        startStr: arg.startStr,
        endStr: arg.endStr,
        viewType: arg.view.type,
      });
      didInitDatesSetRef.current = true;
    },
    [internalView, onRangeChange]
  );

  const safeSelectedIds = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds.map(String) : []),
    [selectedIds]
  );
  const excludedSet = useMemo(
    () => new Set((excludedIds ?? []).map(String)),
    [excludedIds]
  );

  const initialView = useMemo(() => {
    if (viewMode === 'today') return 'timeGridDay';
    if (viewMode === 'week') return 'timeGridWeek';
    if (viewMode === 'month') return 'dayGridMonth';
    return 'timeGridWeek';
  }, [viewMode]);

  const openMoreForDate = useCallback(
    (
      date: Date,
      slotStart: string | null = null,
      slotEnd: string | null = null,
      source?: 'header' | 'slot' | 'month'
    ) => {
      const dateStr = dayjs(date).format('YYYY-MM-DD');
      const agg = filteredData.find((a) => a.date === dateStr);
      if (!agg) return;

      onMoreClick?.({
        dateStr,
        eventDatetime: dateStr,
        shipments: agg.top_shipments,
        slotStart,
        slotEnd,
        source,
      });
    },
    [filteredData, onMoreClick]
  );

  // Month: one all-day event per day; Week/Day: one timed event per hour slot
  const events = useMemo<EventInput[]>(() => {
    if (internalView === 'dayGridMonth') {
      return (filteredData ?? []).map((agg) => ({
        id: `agg-${agg.date}`,
        start: agg.date,
        allDay: true,
        display: 'block',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        extendedProps: { aggregate: agg, isHour: false },
      }));
    }

    // Week / Day view — one timed event per non-empty hour slot
    // + one all-day event per day for shipments with no specific time (Time TBD)
    const evts: EventInput[] = [];
    for (const agg of filteredData ?? []) {
      const hourTotal = (agg.hour_aggregates ?? []).reduce(
        (s, h) => s + h.total,
        0
      );
      const timeUnknownTotal = agg.total - hourTotal;

      // Timed events — one per non-empty hour slot
      for (const hourAgg of agg.hour_aggregates ?? []) {
        if (hourAgg.total === 0) continue;
        const hourEnd = dayjs(hourAgg.hour)
          .add(1, 'hour')
          .format('YYYY-MM-DDTHH:mm:ss');
        evts.push({
          id: `hour-${hourAgg.hour}`,
          start: hourAgg.hour,
          end: hourEnd,
          allDay: false,
          display: 'block',
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          extendedProps: {
            aggregate: hourAgg,
            dayDate: agg.date,
            isHour: true,
          },
        });
      }

      // All-day event for shipments without a specific time (Time TBD row)
      if (timeUnknownTotal > 0) {
        const timeTbdAgg = {
          hour: agg.date,
          total: timeUnknownTotal,
          top_shipments: agg.top_shipments.slice(0, 3),
        };
        evts.push({
          id: `tbd-${agg.date}`,
          start: agg.date,
          allDay: true,
          display: 'block',
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          extendedProps: {
            aggregate: timeTbdAgg,
            dayDate: agg.date,
            isHour: true,
          },
        });
      }
    }
    return evts;
  }, [filteredData, internalView]);

  // Check if there are any allDay (Time TBD) events in week/day view
  const hasTimeTbdEvents = useMemo(() => {
    if (internalView === 'dayGridMonth') return true;
    return events.some((e) => e.allDay);
  }, [events, internalView]);

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const isHour = info.event.extendedProps.isHour as boolean;
      if (isHour) {
        const hourAgg = info.event.extendedProps
          .aggregate as CalendarHourAggregate;
        const dayDate = info.event.extendedProps.dayDate as string;
        const slotEnd = dayjs(hourAgg.hour)
          .add(1, 'hour')
          .format('YYYY-MM-DDTHH:mm:ss');
        onMoreClick?.({
          dateStr: dayDate,
          eventDatetime: hourAgg.hour,
          shipments: hourAgg.top_shipments,
          slotStart: hourAgg.hour,
          slotEnd,
          source: 'slot',
        });
      } else {
        const agg = info.event.extendedProps.aggregate as
          CalendarDayAggregate | undefined;
        if (!agg) return;
        onMoreClick?.({
          dateStr: agg.date,
          eventDatetime: agg.date,
          shipments: agg.top_shipments,
          slotStart: null,
          slotEnd: null,
          source: 'month',
        });
      }
    },
    [onMoreClick]
  );

  const renderShipmentPill = useCallback(
    (shipment: CalendarShipmentItem) => {
      const eventId = String(shipment.shipment_id);
      const reference = getCustomerReference(shipment);
      const isSelected = isSelectAllMode
        ? !excludedSet.has(eventId)
        : safeSelectedIds.includes(eventId);
      const borderColor = getCalendarShipmentBorderColor(
        shipment.status,
        shipment.shipment_type,
        enableCustomerLeg
      );
      const teams = mapTeams(shipment);

      return (
        <Tooltip
          placement='top'
          title={<ReferenceTooltipContent shipment={shipment} />}
          color='transparent'
          overlayInnerStyle={{ padding: 0, boxShadow: 'none' }}
          getPopupContainer={() => document.body}
          zIndex={1000}
        >
          <div
            key={eventId}
            className={`${styles.shipmentTile} ${internalView == 'timeGridThreeDay' || internalView == 'timeGridDay' ? styles['shipmentTile-table-view-three-days'] : ''} ${tableView ? styles['shipmentTile-table-view'] : ''} w-full`}
            style={
              {
                border: `2px solid ${borderColor}`,
              } as React.CSSProperties
            }
            onClick={(e) => {
              e.stopPropagation();
              onShipmentClick?.(eventId, shipment.alert_name, shipment);
            }}
          >
            <Flex className='min-width-0' align='center' gap={4}>
              {checked && (
                <button
                  type='button'
                  className={`${styles.checkbox} ${
                    isSelected ? styles.checkboxSelected : ''
                  }`}
                  style={
                    { '--shipment-border': borderColor } as React.CSSProperties
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.(shipment, !isSelected);
                  }}
                >
                  {isSelected ? <CheckOutlined /> : ''}
                </button>
              )}
              <Flex
                className={
                  tableView &&
                  !['timeGridDay', 'timeGridThreeDay'].includes(internalView)
                    ? 'sm-d-none min-width-0'
                    : 'min-width-0'
                }
                align='center'
                gap={4}
              >
                {tableView ? (
                  <Typography.Text
                    ellipsis={{ tooltip: reference }}
                    className={`${styles.referenceNumber} ${
                      tableView ? styles['referenceNumber-table-view'] : ''
                    }`}
                  >
                    {reference}
                  </Typography.Text>
                ) : (
                  <span className={styles.referenceNumber}>
                    {truncateRef(reference, REF_TRUNCATE_LEN).short}
                  </span>
                )}
              </Flex>
            </Flex>

            {/* desktop responsive  */}
            <Flex className={tableView && 'sm-d-none'} align='center' gap={4}>
              {(shipment.type === ShipmentAlertType.CRITICAL ||
                shipment.type === ShipmentAlertType.WARNING) && (
                <span className={styles.alertIcon}>
                  <AlertIcon type={shipment.type} />
                </span>
              )}

              <span className={styles.spacer} />
              <TeamDots teams={teams} maxVisible={2} tableView={tableView} />
            </Flex>

            {/* mobile responsive  */}

            {tableView && (
              <Flex className='sm-d-inline-flex' align='center' gap={4}>
                {internalView === 'timeGridThreeDay' ||
                internalView === 'timeGridDay' ? (
                  <>
                    {(shipment.type === ShipmentAlertType.CRITICAL ||
                      shipment.type === ShipmentAlertType.WARNING) && (
                      <span className={styles.alertIcon}>
                        <AlertIcon type={shipment.type} />
                      </span>
                    )}

                    <TeamDots
                      teams={teams}
                      maxVisible={1}
                      tableView={tableView}
                    />
                  </>
                ) : shipment.type === ShipmentAlertType.CRITICAL ||
                  shipment.type === ShipmentAlertType.WARNING ? (
                  <span className={styles.alertIcon}>
                    <AlertIcon type={shipment.type} />
                  </span>
                ) : (
                  <TeamDots
                    teams={teams}
                    maxVisible={1}
                    tableView={tableView}
                  />
                )}
              </Flex>
            )}
          </div>
        </Tooltip>
      );
    },
    [
      checked,
      onShipmentClick,
      onToggleSelect,
      safeSelectedIds,
      isSelectAllMode,
      excludedSet,
      tableView,
      internalView,
      enableCustomerLeg,
    ]
  );

  const renderEventContent = useCallback(
    (arg: EventContentArg) => {
      const isHour = arg.event.extendedProps.isHour as boolean;
      const agg = arg.event.extendedProps.aggregate as
        CalendarDayAggregate | CalendarHourAggregate | undefined;
      if (!agg) return null;

      const sorted = sortByEventTimeAsc(agg.top_shipments);

      const showAllThree = Number(agg.total ?? 0) <= 3;
      const visible = showAllThree ? sorted : sorted.slice(0, 2);

      const overflow = showAllThree
        ? 0
        : Math.max(0, Number(agg.total ?? 0) - 2);

      const handleMoreClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isHour) {
          const hourAgg = agg as CalendarHourAggregate;
          const dayDate = arg.event.extendedProps.dayDate as string;
          const slotEnd = dayjs(hourAgg.hour)
            .add(1, 'hour')
            .format('YYYY-MM-DDTHH:mm:ss');
          onMoreClick?.({
            dateStr: dayDate ?? hourAgg.hour.slice(0, 10),
            eventDatetime: hourAgg.hour,
            shipments: hourAgg.top_shipments,
            slotStart: hourAgg.hour,
            slotEnd,
            source: 'slot',
          });
        } else {
          const dayAgg = agg as CalendarDayAggregate;
          onMoreClick?.({
            dateStr: dayAgg.date,
            eventDatetime: dayAgg.date,
            shipments: dayAgg.top_shipments,
            slotStart: null,
            slotEnd: null,
            source: 'month',
          });
        }
      };

      const isTimeGrid =
        internalView === 'timeGridWeek' || internalView === 'timeGridDay';

      return (
        <div
          className={`${styles.groupedContainer} ${tableView && styles['groupedContainer-table-view']} ${
            isTimeGrid ? styles.timeGridEventPadding : ''
          }`}
        >
          {visible.map((shipment) => renderShipmentPill(shipment))}
          {overflow > 0 && (
            <div className={styles.moreLinkInside} onClick={handleMoreClick}>
              +{overflow}{' '}
              <span className={tableView && 'sm-d-none'}> more </span>
            </div>
          )}
        </div>
      );
    },
    [onMoreClick, renderShipmentPill, internalView, tableView]
  );

  useEffect(() => {
    if (internalView !== 'timeGridWeek') return;

    const api = calRef.current?.getApi();
    if (!api) return;

    const rangeStart = api.view.activeStart;
    const rangeEnd = api.view.activeEnd;

    const visibleShipments = getVisibleWeekShipmentsFromHourAggregates(
      filteredData,
      rangeStart,
      rangeEnd
    );

    const scrollTime = getScrollTimeFromVisibleWeekShipments(
      visibleShipments,
      DEFAULT_SCROLL_TIME
    );

    requestAnimationFrame(() => {
      requestAnimationFrame(() => api.scrollToTime(scrollTime));
    });
  }, [internalView, filteredData]);
  useEffect(() => {
    const calendarEl = document.querySelector('.fc-timegrid');
    if (!calendarEl) return;
    const handleMouseEnter = (e: Event) => {
      const header = (e.target as HTMLElement).closest('.fc-col-header-cell');
      if (!header) return;
      const colIndex = Array.from(header.parentElement!.children).indexOf(
        header
      );
      const columns =
        calendarEl.querySelectorAll<HTMLDivElement>('.fc-timegrid-col');
      if (columns[colIndex]) columns[colIndex].classList.add('fc-col-hover');
    };
    const handleMouseLeave = (e: Event) => {
      const header = (e.target as HTMLElement).closest('.fc-col-header-cell');
      if (!header) return;
      const colIndex = Array.from(header.parentElement!.children).indexOf(
        header
      );
      const columns =
        calendarEl.querySelectorAll<HTMLDivElement>('.fc-timegrid-col');
      if (columns[colIndex]) columns[colIndex].classList.remove('fc-col-hover');
    };
    calendarEl.addEventListener('mouseover', handleMouseEnter);
    calendarEl.addEventListener('mouseout', handleMouseLeave);
    return () => {
      calendarEl.removeEventListener('mouseover', handleMouseEnter);
      calendarEl.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [internalView]);

  // Hide the all-day row only for mobile table view; list view keeps it.
  // Month view (dayGridMonth) ignores
  // this prop — all events are placed in day cells automatically.
  const hasAllDayEvents =
    internalView === 'dayGridMonth' ? (filteredData ?? []).length > 0 : true;

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const calendarHeight =
    isMobile && tableView
      ? `calc(100dvh - ${viewportHeight < 600 ? 300 : 440}px)`
      : '60vh';
  // const calendarHeight = isMobile && tableView ? 'calc(100vh - 440px)' : '60vh';
  // const calendarHeight = isMobile && tableView ? '35vh' : '60vh';
  const calendarContentHeight = isMobile && tableView ? 300 : 600;

  return (
    <div
      className={`${styles.calendarWrap} ct-calender-wrapper ${tableView && 'is-table-view'} ${!hasTimeTbdEvents ? ' no-tbd-events' : ''}`}
    >
      <CalendarToolbar
        tableView={tableView}
        viewBy={viewBy}
        setViewBy={setViewBy}
        calendarApi={calRef.current?.getApi()}
        onTodayOpenMore={() => openMoreForDate(new Date())}
        legType={legType}
      />

      <FullCalendar
        ref={(r: FullCalendar | null) => {
          calRef.current = r;
          if (calendarApiRef) {
            calendarApiRef.current = r?.getApi?.() ?? null;
          }
        }}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={initialView}
        headerToolbar={false}
        height={calendarHeight}
        stickyHeaderDates={true}
        contentHeight={calendarContentHeight}
        slotEventOverlap={false}
        expandRows={false}
        fixedWeekCount={false}
        dayMaxEvents={false}
        navLinks={internalView === 'timeGridWeek'}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navLinkDayClick={(date: Date, jsEvent: any) => {
          jsEvent.preventDefault();
          jsEvent.stopPropagation();

          const viewType = calRef.current?.getApi().view.type;
          if (viewType !== 'timeGridWeek') return;

          openMoreForDate(date, null, null, 'header');
        }}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dateClick={(info: any) => {
          const viewType = calRef.current?.getApi().view.type;
          const source =
            viewType === 'timeGridWeek' || viewType === 'timeGridDay'
              ? 'slot'
              : 'month';
          openMoreForDate(info.date, null, null, source);
        }}
        eventDisplay='block'
        slotDuration={'01:00:00'}
        slotMinTime={'00:00:00'}
        slotMaxTime={'24:00:00'}
        dayHeaderFormat={{ weekday: 'short' }}
        views={{
          // timeGridWeek: { dayHeaderFormat: { weekday: 'short' } },
          // timeGridDay: { dayHeaderFormat: { weekday: 'short' } },
          timeGridDay: {
            type: 'timeGrid',
            duration: { days: 1 },
            dayHeaderFormat: { weekday: 'short' },
          },
          timeGridThreeDay: {
            type: 'timeGrid',
            duration: { days: 3 },
            dayHeaderFormat: { weekday: 'short' },
          },
          timeGridWeek: {
            type: 'timeGridWeek',
            dayHeaderFormat: { weekday: 'short' },
          },
          dayGridMonth: {
            type: 'dayGridMonth',
          },
        }}
        weekNumbers={
          internalView === 'timeGridWeek' ||
          internalView === 'timeGridThreeDay' ||
          internalView === 'timeGridDay'
        }
        weekNumberContent={() => (
          <span className={`${styles.moreLink} mx-2`}>
            {userTimeZoneString}
          </span>
        )}
        allDaySlot={!(isMobile && tableView) && hasAllDayEvents}
        allDayText={'Time TBD'}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dayCellContent={function (arg: any) {
          return {
            html: `<div class="custom-date-right">${arg.date.getDate()}</div>`,
          };
        }}
        weekends
        events={events}
        eventContent={renderEventContent}
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
        timeZone='local'
      />
      {/* on small screeen only ( < 768) */}
      {tableView && (
        <Flex
          className='sm-d-inline-flex responsive-gap-12 py-3 w-full legends-full-width'
          justify='center'
          align='center'
          wrap
        >
          <ColorLegends legType={legType} />
        </Flex>
      )}
    </div>
  );
}
